import { Box, Flex, Stack } from '@chakra-ui/react';
import { VOUCHER_REWARD_TYPE } from '@diamond/shared/types';
import { PromoPriceIntegration } from '@diamond/shared/ui';
import {
  formatDiscountAmountAbbreviated,
  processVoucherData,
  ProductWithPromo,
} from '@diamond/sol-buyer/utils';

import { CheckIconVoucher } from '../carts/Icons';

interface OrderDetailPromoPriceProps {
  product: ProductWithPromo;
  isVoucherMenuEnabled: boolean;
}

export const OrderDetailPromoPrice = ({
  product,
  isVoucherMenuEnabled,
}: OrderDetailPromoPriceProps) => {
  const voucherData = processVoucherData(product);
  const {
    hasVoucherDiscount,
    hasVoucherFreeGoods,
    voucherDiscount,
    discountPercent,
    discountAmount,
    voucherFreeGoods,
  } = voucherData;

  const hasActualVoucherDiscount =
    hasVoucherDiscount && voucherData.voucherDiscount !== null;
  const hasActualVoucherFreeGoods =
    hasVoucherFreeGoods && voucherFreeGoods.length > 0;
  const hasAnyVoucher = hasActualVoucherDiscount || hasActualVoucherFreeGoods;

  const hasDiscount = product.hasDiscount;

  const discountPromo = hasDiscount
    ? product.compiled_discount?.find((promo) => promo.is_discount) ?? null
    : null;

  // Only show discount details, not cashback/free goods badges
  const showDiscountDetails = hasDiscount || hasActualVoucherDiscount;

  if (!showDiscountDetails) {
    return null;
  }

  const productPrice = product.latest_price || product.price || 0;

  /**
   * Calculates the promo deduction amount with the following priority:
   * 1. Voucher percent discount (discountPercent * 100) - when voucher has percent off
   * 2. Voucher amount discount ((discountAmount / productPrice) * 10000) - when voucher has fixed amount off
   * 3. Promo discount from compiled_discount - fallback for non-voucher promos
   *
   * Returns undefined when no applicable discount is found.
   */
  let promoDeductionAmount: number | undefined;
  if (hasActualVoucherDiscount && discountPercent !== null) {
    promoDeductionAmount = discountPercent * 100;
  } else if (
    hasActualVoucherDiscount &&
    discountAmount !== null &&
    productPrice > 0
  ) {
    promoDeductionAmount = (discountAmount / productPrice) * 10000;
  } else {
    promoDeductionAmount = discountPromo?.deduction_amount;
  }

  const badgeLabelOverride =
    hasActualVoucherDiscount &&
    voucherDiscount?.reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT &&
    voucherDiscount.discount_amount !== null
      ? formatDiscountAmountAbbreviated(voucherDiscount.discount_amount)
      : undefined;

  const shouldShowVoucherUI = isVoucherMenuEnabled;

  const effectivePrice = product.latest_price ?? product.price ?? 0;
  const hasPrice = Boolean(product.latest_price || product.price);
  const prices = hasPrice
    ? [
        {
          price: effectivePrice,
          min_order_quantity: product.minimum_order_quantity ?? 0,
        },
      ]
    : [];

  const isBundled =
    product.compiled_discount?.some((promo) => !promo.is_discount) ?? false;

  return (
    <Stack
      spacing={{ base: '0', lg: '1' }}
      alignItems={{ base: 'flex-start', lg: 'flex-start' }}
      w={{ base: 'auto', lg: 'auto' }}
      id="order-detail-promo-price-container"
    >
      <Flex gap="2" alignItems="center" flexWrap="nowrap">
        {shouldShowVoucherUI && hasAnyVoucher && (
          <CheckIconVoucher
            boxSize={{ base: '16px', lg: '20px' }}
            flexShrink={0}
          />
        )}
        <Box flexShrink={1}>
          <PromoPriceIntegration
            data={{
              ...product,
              prices,
              minimum_order_quantity: product.minimum_order_quantity ?? 0,
              discount_promos: [],
              bundled_discount_promos: [],
              bundled_free_good_promos: [],
            }}
            promoDeductionAmount={promoDeductionAmount}
            promoIsDiscount={hasActualVoucherDiscount || hasDiscount}
            isBundled={!hasActualVoucherDiscount && isBundled}
            hasActiveDiscount={true}
            badgeLabelOverride={badgeLabelOverride}
          />
        </Box>
      </Flex>
    </Stack>
  );
};
